import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { updateInvoiceTax } from '../../api_handlers/invoice';
import { money } from './kit';
import {
  TDS_SECTIONS,
  hasTax,
  netPayable,
  taxPayload,
  validateTax,
} from './lib/tax';
import { payeeOf } from './lib/paymentRun';

/**
 * The finance desk on one invoice (spec §5, subsystem 2).
 *
 * What a creator invoices is not what reaches their bank. Finance enters the
 * taxable value and the section; the net is derived and never stored, the same
 * rule margin follows on the project header.
 *
 * Business approves and finance pays — finance does not re-approve (D7) — so
 * this block never offers a status move. It only ever edits figures.
 *
 * The endpoint does not exist yet. Until it does, the block shows the net it
 * can estimate from `amount` and says that is what it is, rather than printing
 * a guess as though it were a computation.
 */

export const TAX_CSS = `
.tx-card { border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); padding: 14px 16px; }
.tx-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.tx-title { font-size: 12px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-4); margin: 0; }
.tx-rows { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 7px; }
.tx-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; font-size: 13px; color: var(--ink-3); }
.tx-row__label { display: flex; align-items: baseline; gap: 6px; }
.tx-row__hint { font-size: 11px; color: var(--ink-4); }
.tx-row--total { border-top: 1px solid var(--line); margin-top: 4px; padding-top: 9px; font-size: 14.5px; font-weight: 800; color: var(--ink); }
.tx-row--minus .tx-row__value { color: #B42318; }
.tx-note { font-size: 12px; line-height: 1.5; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: var(--radius-sm); padding: 9px 11px; margin: 12px 0 0; }
.tx-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin-top: 13px; }
.tx-form__wide { grid-column: 1 / -1; }
.tx-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 13px; }
.tx-preview { border: 1px dashed var(--line); border-radius: var(--radius-sm); padding: 10px 12px; margin-top: 12px; display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 13px; }
.tx-preview__value { font-size: 15px; font-weight: 800; color: var(--ink); }
`;

const TEXT_FIELDS = [
  { key: 'supplier_gstin', label: 'Creator GSTIN', placeholder: '33AAIFF6389B1ZG' },
  { key: 'pan', label: 'PAN', placeholder: 'AAIFF6389B' },
];

/** The fields the form owns, seeded from the invoice and its payout record. */
function formFrom(invoice) {
  const record = invoice || {};
  const payee = payeeOf(record);
  const value = (key, fallback = '') =>
    record[key] === null || record[key] === undefined ? fallback : String(record[key]);

  return {
    taxable_value: value('taxable_value', value('amount')),
    supplier_gstin: value('supplier_gstin', payee.supplier_gstin || ''),
    pan: value('pan', payee.pan || ''),
    entity_type: value('entity_type', 'individual'),
    gst_rate: value('gst_rate'),
    tds_section: value('tds_section'),
    tds_rate: value('tds_rate'),
  };
}

function Row({ label, hint, value, variant }) {
  return (
    <li className={`tx-row${variant ? ` tx-row--${variant}` : ''}`}>
      <span className="tx-row__label">
        {label}
        {hint ? <span className="tx-row__hint">{hint}</span> : null}
      </span>
      <span className="tx-row__value iv-num">{value}</span>
    </li>
  );
}

const gstLabel = (gst) => {
  if (gst.kind === 'igst') return 'IGST';
  if (gst.kind === 'cgst_sgst') return 'CGST + SGST';
  return 'GST';
};

const percent = (rate) => `${Math.round(rate * 10000) / 100}%`;

/** The computed breakdown — the same figures the payment run will use. */
export function TaxBreakdown({ invoice }) {
  const tax = useMemo(() => {
    const record = invoice || {};
    const payee = payeeOf(record);
    // What finance entered on the invoice wins; the payout record only fills
    // the gaps. Overriding unconditionally would wipe an entered PAN with
    // undefined and silently deduct TDS at the no-PAN rate.
    return netPayable({
      ...record,
      pan: record.pan || payee.pan,
      supplier_gstin: record.supplier_gstin || payee.supplier_gstin,
    });
  }, [invoice]);

  return (
    <>
      <ul className="tx-rows">
        <Row label="Taxable value" value={money(tax.taxable)} />
        {tax.gst.total > 0 ? (
          <Row
            label={gstLabel(tax.gst)}
            hint={percent(tax.gst.rate)}
            value={money(tax.gst.total)}
          />
        ) : (
          <Row label="GST" hint="not registered" value="—" />
        )}
        {tax.tds.amount > 0 ? (
          <Row
            label="TDS"
            hint={`${tax.tds.section} · ${percent(tax.tds.rate)}`}
            value={`− ${money(tax.tds.amount)}`}
            variant="minus"
          />
        ) : (
          <Row label="TDS" hint={tax.tds.reason} value="—" />
        )}
        <Row label="Net payable" value={money(tax.net)} variant="total" />
      </ul>

      {tax.estimated ? (
        <p className="tx-note">
          Estimated from the invoice amount — no taxable value has been entered, so this is
          not a tax computation and should not be paid from.
        </p>
      ) : null}
    </>
  );
}

export default function TaxFields({ invoice, canEdit = false, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => formFrom(invoice));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const preview = useMemo(() => netPayable(form), [form]);

  async function save() {
    const found = validateTax(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      const updated = await updateInvoiceTax(invoice.id, taxPayload(form));
      toast.success('Tax details saved');
      setEditing(false);
      if (onSaved) onSaved(updated);
    } catch (error) {
      if (error.status === 404) {
        toast.error('Tax fields are not available on the server yet.');
      } else {
        toast.error(error.message || 'Could not save the tax details');
      }
      setErrors(error.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  }

  function startEditing() {
    setForm(formFrom(invoice));
    setErrors({});
    setEditing(true);
  }

  if (editing) {
    return (
      <section className="tx-card" aria-label="Tax details">
        <div className="tx-head">
          <p className="tx-title">Tax details</p>
        </div>

        <div className="tx-form">
          <label className="iv-field">
            <span className="iv-label">Taxable value (₹)</span>
            <input
              className="iv-input iv-num"
              type="number"
              min="0"
              value={form.taxable_value}
              onChange={set('taxable_value')}
            />
            {errors.taxable_value ? <span className="iv-error" role="alert">{errors.taxable_value}</span> : null}
          </label>

          {TEXT_FIELDS.map((field) => (
            <label className="iv-field" key={field.key}>
              <span className="iv-label">{field.label}</span>
              <input
                className="iv-input"
                value={form[field.key]}
                placeholder={field.placeholder}
                onChange={set(field.key)}
              />
              {errors[field.key] ? <span className="iv-error" role="alert">{errors[field.key]}</span> : null}
            </label>
          ))}

          <label className="iv-field">
            <span className="iv-label">They invoice as</span>
            <select className="iv-input" value={form.entity_type} onChange={set('entity_type')}>
              <option value="individual">An individual</option>
              <option value="company">A company</option>
            </select>
          </label>

          <label className="iv-field">
            <span className="iv-label">TDS section</span>
            <select className="iv-input" value={form.tds_section} onChange={set('tds_section')}>
              <option value="">No TDS</option>
              {TDS_SECTIONS.map((section) => (
                <option key={section.code} value={section.code}>
                  {section.code} — {section.label}
                </option>
              ))}
            </select>
            {errors.tds_section ? <span className="iv-error" role="alert">{errors.tds_section}</span> : null}
          </label>

          <label className="iv-field">
            <span className="iv-label">Override GST rate</span>
            <input
              className="iv-input iv-num"
              type="number"
              step="0.01"
              min="0"
              max="1"
              placeholder="0.18"
              value={form.gst_rate}
              onChange={set('gst_rate')}
            />
            {errors.gst_rate ? <span className="iv-error" role="alert">{errors.gst_rate}</span> : null}
          </label>

          <label className="iv-field">
            <span className="iv-label">Override TDS rate</span>
            <input
              className="iv-input iv-num"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={form.tds_rate}
              onChange={set('tds_rate')}
            />
            {errors.tds_rate ? <span className="iv-error" role="alert">{errors.tds_rate}</span> : null}
          </label>
        </div>

        <div className="tx-preview">
          <span>Net payable on these figures</span>
          <span className="tx-preview__value iv-num">{money(preview.net)}</span>
        </div>

        <div className="tx-actions">
          <button type="button" className="iv-btn iv-btn--ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={save} disabled={saving}>
            {saving ? <span className="iv-spin" /> : null}
            Save tax details
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="tx-card" aria-label="Tax details">
      <div className="tx-head">
        <p className="tx-title">{hasTax(invoice) ? 'Tax details' : 'Payable'}</p>
        {canEdit ? (
          <button
            type="button"
            className="iv-btn iv-btn--ghost iv-btn--sm"
            onClick={startEditing}
          >
            {hasTax(invoice) ? 'Edit' : 'Enter tax details'}
          </button>
        ) : null}
      </div>
      <TaxBreakdown invoice={invoice} />
    </section>
  );
}
